import {
  createEntityAdapter,
  createSlice,
  EntityState,
  PayloadAction,
} from '@reduxjs/toolkit';

import { Account } from 'protocol/api/billing/dto_account_pb';
import { AdminUserInfoStreamResp_OrganizationUserSharingInfo } from 'protocol/api/core/svc_adminka_pb';
import { Organization } from 'protocol/model/dto_organization_pb';
import { OrganizationStaff } from 'protocol/model/dto_organization_staff_pb';
import { User } from 'protocol/model/dto_user_pb';

import { LoadingStateType } from '@/shared/config';

import { getInitialUserSettings } from '../lib/getInitialUserSettings';

import { setSettings } from './userSlice.thunks';


export type UserOrganizationLegacy = {
  id: string;
  name: string;
  isCurrentUserAdmin: boolean;
};

export const organizationUserSharingInfoAdapter =
  createEntityAdapter<AdminUserInfoStreamResp_OrganizationUserSharingInfo>({
    selectId: (sharedOrganization) => sharedOrganization.OrganizationID,
  });

type UserSliceState = {
  user: User;
  currentUser: User;
  currentUserStaff: OrganizationStaff[];
  currentUserOrganizationNames: Record<string, string>;
  adminUserOrganizations: Organization[];
  userOrganizationLegacy: UserOrganizationLegacy;
  billingAccount: Account;
  settings: ReturnType<typeof getInitialUserSettings>;
  loading: boolean;
  currentUserLoadingState: LoadingStateType;
};

type UserAdapterState = EntityState<AdminUserInfoStreamResp_OrganizationUserSharingInfo> & UserSliceState;

const initialState: UserSliceState = {
  // TODO: Rename user to admin or move into another slice
  user: {} as User,
  currentUser: {} as User,
  currentUserStaff: [],
  currentUserOrganizationNames: {},
  adminUserOrganizations: [],
  userOrganizationLegacy: {
    id: '',
    name: '',
    isCurrentUserAdmin: true,
  },
  billingAccount: {} as Account,
  settings: getInitialUserSettings(),
  loading: false,
  currentUserLoadingState: 'idle',
};

const userSlice = createSlice({
  name: 'user',
  initialState:
    organizationUserSharingInfoAdapter.getInitialState(initialState) as UserAdapterState,
  reducers: {
    // @ts-ignore
    updateOne: organizationUserSharingInfoAdapter.updateOne,
    // @ts-ignore
    setMany: organizationUserSharingInfoAdapter.setMany,
    // @ts-ignore
    removeAll: organizationUserSharingInfoAdapter.removeAll,
    addUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    resetUser: (state) => {
      state.user = {} as User;
    },
    setCurrentUser: (state, { payload }: PayloadAction<User>) => {
      state.currentUser = payload;
    },
    setCurrentUserOrganizations: (
      state,
      { payload }: PayloadAction<OrganizationStaff[]>,
    ) => {
      state.currentUserStaff = payload;
    },
    setCurrentUserOrganizationNames: (
      state,
      { payload }: PayloadAction<Record<string, string>>,
    ) => {
      state.currentUserOrganizationNames = payload;
    },
    addOrganizationName: (
      state,
      { payload }: PayloadAction<{ id: string; name: string }>,
    ) => {
      state.currentUserOrganizationNames[payload.id] = payload.name;
    },
    setNewestUserOrganization: (
      state,
      { payload }: PayloadAction<OrganizationStaff>,
    ) => {
      const existingIndex = state.currentUserStaff.findIndex(
        (staff: OrganizationStaff) => staff.ID === payload.ID,
      );

      if (existingIndex === -1) {
        state.currentUserStaff.push(payload);
        return;
      }

      const currentRevision = state.currentUserStaff[existingIndex]?.Revision?.Number ?? 0;
      const payloadRevision = payload.Revision?.Number ?? 0;

      if (payloadRevision > currentRevision) {
        state.currentUserStaff[existingIndex] = payload;
      }
    },
    addNewOrganization: (state, { payload }: PayloadAction<OrganizationStaff>) => {
      state.currentUserStaff.push(payload);
    },
    setUserOrganizationLegacy: (
      state,
      action: PayloadAction<UserOrganizationLegacy>,
    ) => {
      state.userOrganizationLegacy = action.payload;
    },
    setBillingAccount: (state, action: PayloadAction<Account>) => {
      state.billingAccount = action.payload;
    },
    resetCurrentUser: (state) => {
      state.currentUser = {} as User;
    },
    resetCurrentUserOrganizations: (state) => {
      state.currentUserStaff = [];
      state.currentUserOrganizationNames = {};
    },
    setAdminUserOrganizations: (
      state,
      { payload }: PayloadAction<Organization[]>,
    ) => {
      state.adminUserOrganizations = payload;
    },
    setLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setCurrentUserLoadingState: (
      state,
      { payload }: PayloadAction<LoadingStateType>,
    ) => {
      state.currentUserLoadingState = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setSettings.fulfilled, (state, action) => {
      state.settings = { ...state.settings, ...action.payload };
    });
  },
});

export const { actions } = userSlice;

export default userSlice.reducer;
